import React, { useState, useEffect } from 'react';
import ProjectCard from './projectCard';
import OpinionCard from './opinionCard';

const Carousel = ({ elements, cardType, color }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    // Minimum distance needed to trigger a slide change
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onMouseDown = (e) => {
        setTouchEnd(null);
        setTouchStart(e.clientX);
        setIsDragging(true);
    };

    const onMouseMove = (e) => {
        if (isDragging) {
            setTouchEnd(e.clientX);
        }
    };

    const handleSwipeEnd = () => {
        setIsDragging(false);
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            nextSlide();
        } else if (isRightSwipe) {
            prevSlide();
        }

        // Reset values
        setTouchStart(null);
        setTouchEnd(null);
    };

    const onMouseLeave = () => {
        if (isDragging) {
            handleSwipeEnd();
        }
    };

    // Use a ref to prevent multiple rapid scroll events from triggering multiple slides
    const lastScrollTime = React.useRef(0);

    const handleWheel = (e) => {
        const now = Date.now();
        // Prevent rapid scroll triggering (cooldown of 400ms)
        if (now - lastScrollTime.current < 400) {
            return;
        }

        // isHorizontalScroll detects native trackpad horizontal swiping
        const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);

        if (isHorizontalScroll) {
            // Trackpad horizontal scroll
            if (e.deltaX > minSwipeDistance) {
                nextSlide();
                lastScrollTime.current = now;
            } else if (e.deltaX < -minSwipeDistance) {
                prevSlide();
                lastScrollTime.current = now;
            }
        } else if (e.shiftKey) {
            // Mouse wheel scroll + Shift key for horizontal movement
            // When holding shift, some browsers map wheel to deltaX, but some still map to deltaY
            // We'll check which one had the larger movement that broke the threshold
            const scrollDelta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;

            if (scrollDelta > minSwipeDistance) {
                nextSlide();
                lastScrollTime.current = now;
            } else if (scrollDelta < -minSwipeDistance) {
                prevSlide();
                lastScrollTime.current = now;
            }
        }
    };

    useEffect(() => {
        // Function to determine starting index based on screen size
        const handleResize = () => {
            if (window.matchMedia('(min-width: 1024px)').matches) {
                setCurrentIndex(1); // Start from 3nd item for medium and larger screens
            } else {
                setCurrentIndex(0); // Start from 1st item for smaller screens
            }
        };

        // Set initial index and add event listener for resizing
        handleResize();
        window.addEventListener('resize', handleResize);

        // Cleanup event listener
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const nextSlide = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === elements.length - 1 ? prevIndex : prevIndex + 1
        );
    };

    const prevSlide = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? prevIndex : prevIndex - 1
        );
    };

    const renderCard = (element) => {
        // Conditionally render the card based on cardType
        if (cardType === 'project') {
            return <ProjectCard {...element} />;
        }
        if (cardType === 'opinion') {
            return <OpinionCard {...element} />;
        }
        return null; // Fallback for unsupported cardType
    };

    return (
        <div className="w-full max-w-[350px] md:max-w-lg">
            {/* Carousel Content */}
            <div
                className={`flex transition-transform duration-500 ease-in-out cursor-grab active:cursor-grabbing`}
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={handleSwipeEnd}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={handleSwipeEnd}
                onMouseLeave={onMouseLeave}
                onWheel={handleWheel}
            >
                {elements.map((element, index) => (
                    <div
                        key={index}
                        className="flex-shrink-0 w-full px-2 md:px-4 flex items-stretch"
                    >
                        {renderCard(element)}
                    </div>
                ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-end items-center gap-x-4 md:gap-x-6 mt-10 md:mt-16 px-4 md:px-8">
                {/* Previous Button */}
                <button
                    onClick={prevSlide}
                    disabled={currentIndex === 0}
                    aria-label="Previous Slide"
                    className={`custom-${color}-btn !h-14 !w-14 md:!h-16 md:!w-16 disabled:opacity-50 disabled:pointer-events-none`}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-7 md:size-8"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                        />
                    </svg>
                </button>

                {/* Next Button */}
                <button
                    onClick={nextSlide}
                    disabled={currentIndex === elements.length - 1}
                    aria-label="Next Slide"
                    className={`custom-${color}-btn !h-14 !w-14 md:!h-16 md:!w-16 disabled:opacity-50 disabled:pointer-events-none`}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-7 md:size-8"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default Carousel;
